// The four profit reports (Date / Item / Category / Group wise) on the report
// kit. Each loads Api/Reports/<Kind>ProfitReport/GetAllLazyPagedData for the
// header's branch and the picked period; the server works out cost (WAC at the
// moment of sale), profit, margin and markup per row.
//
// Rows other than date-wise carry no date, so they are stamped with the loaded
// period's last day - that keeps them inside the kit's period filter.
import React, { useCallback, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { getProfitReport } from "../../../services/ReportApiService";
import { DatatablePagingParameter, Filterclass } from "../../../core/json/DatatablePagingParameters";
import { ReportScreen } from "../../../components/report";
import { dateOf, num, pct2, sumBy } from "../../../components/report/reportFormat";

const pctCell = (k) => (r) => `${pct2(r[k])}%`;
const FIGURES = [
  { k: "qty", t: "Qty", type: "int" },
  { k: "sales", t: "Total Sales Price", type: "money" },
  { k: "discount", t: "Total Discount", type: "money" },
  { k: "net", t: "Net Sales", type: "money" },
  { k: "cost", t: "Total Cost Price", type: "money" },
  { k: "profit", t: "Profit", type: "money" },
  { k: "margin", t: "Gross Profit %", noSum: true, cell: pctCell("margin") },
  { k: "markup", t: "Markup %", noSum: true, cell: pctCell("markup") },
  { k: "share", t: "Share of Profit", type: "share", shareOf: "profit" },
];

export const KINDS = {
  date: {
    report: "DatewiseProfitReport",
    key: "dateWiseProfitReportDetail",
    title: "Date Wise Profit",
    subtitle: "Profit on every day's sales: net of discounts, before VAT, at the cost on the day of sale",
    unit: "days",
    columns: [{ k: "date", t: "Date", type: "date" }, ...FIGURES],
    sort: { k: "date", d: -1 },
    dated: true,
  },
  item: {
    report: "ItemwiseProfitReport",
    key: "itemWiseProfitReportDetail",
    title: "Item Wise Profit",
    subtitle: "Profit per item sold in the period, at the cost on the day of sale",
    unit: "items",
    columns: [
      { k: "code", t: "Item Code", type: "code" },
      { k: "name", t: "Item Name" },
      { k: "rate", t: "Avg Sales Price", type: "money", noSum: true, off: true },
      { k: "unitCost", t: "Avg Cost Price", type: "money", noSum: true, off: true },
      ...FIGURES,
    ],
    sort: { k: "profit", d: -1 },
  },
  category: {
    report: "CategorywiseProfitReport",
    key: "categoryWiseProfitReportDetail",
    title: "Category Wise Profit",
    subtitle: "Profit per product category in the period",
    unit: "categories",
    columns: [{ k: "name", t: "Category", type: "avatar" }, ...FIGURES],
    sort: { k: "profit", d: -1 },
  },
  group: {
    report: "GroupwiseProfitReport",
    key: "groupWiseProfitReportDetail",
    title: "Group Wise Profit",
    subtitle: "Profit per product group in the period",
    unit: "groups",
    columns: [{ k: "name", t: "Group", type: "avatar" }, ...FIGURES],
    sort: { k: "profit", d: -1 },
  },
};

const nameOf = (kind, r) =>
  kind === "item" ? r.product?.name : kind === "category" ? r.category?.name : kind === "group" ? r.group?.name : "";

export const normProfitRow = (kind, r, i, stamp) => ({
  key: r.product?.guid || r.category?.guid || r.group?.guid || `${kind}-${i}`,
  date: kind === "date" ? dateOf(r.date) : stamp,
  code: r.product?.code || "",
  name: nameOf(kind, r) || "—",
  qty: num(r.quantity),
  sales: num(r.totalSalesPrice),
  discount: num(r.totalDiscount),
  net: num(r.netSalesPrice),
  cost: num(r.totalCostPrice),
  profit: num(r.profit),
  margin: num(r.grossProfit),
  markup: num(r.markup),
  rate: num(r.salesPrice),
  unitCost: num(r.costPrice),
});

const ProfitReport = ({ kind }) => {
  const spec = KINDS[kind];
  const { branch } = useSelector((state) => state.branchlist);
  const [raw, setRaw] = useState({ list: [], stamp: "" });
  const [error, setError] = useState("");
  const period = useRef(["", ""]);
  const req = useRef(0);

  const load = useCallback(async () => {
    if (!branch?.guid) return;
    const id = ++req.current;
    // an open period ("All") still needs dates on the server
    const [from, to] = period.current;
    const DatatablePagingParameters = new DatatablePagingParameter();
    DatatablePagingParameters.PageNumber = 1;
    DatatablePagingParameters.PageSize = -1;
    const Filters = new Filterclass();
    Filters.Branch = branch;
    Filters.FromDate = from || "2000-01-01";
    Filters.ToDate = to || dayjs().format("YYYY-MM-DD");
    try {
      const res = await getProfitReport(spec.report, { DatatablePagingParameters, Filters });
      if (id !== req.current) return;
      // 204 No Content = nothing in the period
      const list = res?.data?.[spec.key];
      setRaw({ list: Array.isArray(list) ? list : [], stamp: Filters.ToDate });
      setError("");
    } catch (e) {
      if (id !== req.current) return;
      console.error(`${spec.title} failed`, e?.response?.data || e);
      setRaw({ list: [], stamp: "" });
      setError(e?.response?.data?.Message || e?.response?.data?.message || "the server did not answer");
    }
  }, [branch, spec]);

  const onPeriodChange = useCallback(
    (from, to) => {
      period.current = [from, to];
      load();
    },
    [load]
  );

  const rows = useMemo(
    () => raw.list.map((r, i) => normProfitRow(kind, r, i, raw.stamp)),
    [raw, kind]
  );

  const tabs = useMemo(
    () => [{ id: "detail", label: spec.title, columns: spec.columns, defaultSort: spec.sort, rowKey: (r) => r.key, drillDown: false }],
    [spec]
  );
  const kpis = useMemo(
    () => [
      { label: "Net sales", value: (l) => sumBy(l, "net") || sumBy(l, "sales") - sumBy(l, "discount") },
      { label: "Cost of sales", value: (l) => sumBy(l, "cost"), invert: true },
      { label: "Profit", value: (l) => sumBy(l, "profit"), hero: true },
      {
        label: "Gross profit %",
        value: (l) => {
          const net = sumBy(l, "net") || sumBy(l, "sales") - sumBy(l, "discount");
          return net ? (sumBy(l, "profit") / net) * 100 : 0;
        },
      },
      {
        label: "Markup %",
        value: (l) => (sumBy(l, "cost") ? (sumBy(l, "profit") / sumBy(l, "cost")) * 100 : 0),
      },
      { label: "Qty sold", value: (l) => sumBy(l, "qty"), format: "int" },
    ],
    []
  );
  const searchMatch = useCallback((r, q) => [r.name, r.code, r.date].some((s) => String(s || "").toLowerCase().includes(q)), []);

  return (
    <ReportScreen
      title={spec.title}
      subtitle={error ? `Could not load the report: ${error}` : spec.subtitle}
      unit={spec.unit}
      rows={rows}
      tabs={tabs}
      kpis={kpis}
      chart={spec.dated ? { valueKey: "profit", title: "Profit over time" } : false}
      searchMatch={searchMatch}
      searchPlaceholder={kind === "date" ? "Date…" : kind === "item" ? "Item name or code…" : "Name…"}
      exportName={spec.title}
      reportNames={{ detail: spec.title }}
      onRefresh={load}
      onPeriodChange={onPeriodChange}
      defaultPreset="tmonth"
    />
  );
};

export default ProfitReport;
